import { useParams } from 'react-router-dom'
import { Typography, Box, IconButton, Tooltip, Badge, Dialog, TextField } from '@mui/material'
import { Close, Edit as EditIcon, Check } from '@mui/icons-material'
import { useProtocolRoomImages } from '@services/protocols/protocols.hook'
import ReactImageGallery from 'react-image-gallery'
import 'react-image-gallery/styles/css/image-gallery.css'
import { InsertPhotoOutlined, DeleteOutline } from '@mui/icons-material'
import { useState } from 'react'

/*
 *  Workaround for ESM projects and support React 19
 *  https://github.com/xiaolin/react-image-gallery/issues/809
 */
const ImageGallery = (ReactImageGallery as any).default
  ? (ReactImageGallery as any).default
  : ReactImageGallery

function ProtocolRoomImages({ roomId }: { roomId: number }) {
  const { id } = useParams<{ id: string }>()
  const { protocolRoomImages, deleteProtocolRoomImages, updateProtocolRoomImages } =
    useProtocolRoomImages(Number(id), roomId)
  const protocolImages = protocolRoomImages?.map((item: { id: number; image: string }) => ({
    id: item.id,
    original: item.image,
    thumbnail: item.image
  }))
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null)
  const [commentValue, setCommentValue] = useState('')

  const handleSlide = (currentIndex: number) => {
    setCurrentImageIndex(currentIndex)
  }

  const getCurrentImageId = () => {
    return protocolRoomImages[currentImageIndex]?.id
  }

  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index)
    setIsGalleryOpen(true)
  }

  const handleEditComment = (imageId: number, currentComment?: string) => {
    setEditingCommentId(imageId)
    setCommentValue(currentComment || '')
  }

  const handleSaveComment = async (imageId: number) => {
    await updateProtocolRoomImages.mutateAsync({
      id: imageId,
      comment: commentValue
    })
    setEditingCommentId(null)
    setCommentValue('')
  }

  const handleCancelEdit = () => {
    setEditingCommentId(null)
    setCommentValue('')
  }

  if (!protocolRoomImages.length) {
    return <Typography variant='body2'>Brak zdjęć</Typography>
  }

  return (
    <Box>
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          maxWidth: '450px'
        }}
      >
        {protocolRoomImages.map(({ image, id, comment }, index) => (
          <Box key={`${image}_${id}`} sx={{ display: 'inline-block', mx: 1, mb: 2 }}>
            <Badge
              color='primary'
              sx={{
                '.MuiBadge-badge': {
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%'
                },
                display: 'block'
              }}
              badgeContent={
                <IconButton
                  disabled={deleteProtocolRoomImages.isPending}
                  size='small'
                  onClick={() => deleteProtocolRoomImages.mutate(id)}
                >
                  <Close fontSize='small' color='secondary' />
                </IconButton>
              }
            >
              <Tooltip title={image} placement='top'>
                <Box
                  onClick={() => handleImageClick(index)}
                  sx={{
                    width: '96px',
                    height: '96px',
                    backgroundImage: `url("${image}")`,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.400',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    cursor: 'pointer'
                  }}
                />
              </Tooltip>
            </Badge>
            {editingCommentId === id ? (
              <Box sx={{ mt: 1, width: '250px' }}>
                <TextField
                  size='small'
                  fullWidth
                  multiline
                  rows={3}
                  value={commentValue}
                  onChange={(e) => setCommentValue(e.target.value)}
                  placeholder='Wpisz komentarz do zdjęcia'
                  autoFocus
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: '0.875rem'
                    }
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
                  <IconButton
                    size='small'
                    onClick={handleCancelEdit}
                    sx={{
                      bgcolor: 'grey.200',
                      '&:hover': { bgcolor: 'grey.300' }
                    }}
                  >
                    <Close fontSize='small' />
                  </IconButton>
                  <IconButton
                    size='small'
                    onClick={() => handleSaveComment(id)}
                    disabled={updateProtocolRoomImages.isPending}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                  >
                    <Check fontSize='small' />
                  </IconButton>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  mt: 1,
                  width: '250px',
                  minHeight: '40px',
                  p: 1,
                  border: '1px solid',
                  borderColor: comment ? 'grey.300' : 'grey.200',
                  borderRadius: 1,
                  bgcolor: comment ? 'background.paper' : 'grey.50',
                  position: 'relative',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                    '& .edit-icon': {
                      opacity: 1
                    }
                  }
                }}
                onClick={() => handleEditComment(id, comment)}
              >
                <Typography
                  variant='body2'
                  sx={{
                    fontSize: '0.875rem',
                    wordBreak: 'break-word',
                    color: comment ? 'text.primary' : 'text.disabled',
                    pr: 3
                  }}
                >
                  {comment || 'Kliknij aby dodać komentarz'}
                </Typography>
                <IconButton
                  className='edit-icon'
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation()
                    handleEditComment(id, comment)
                  }}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    padding: '4px',
                    opacity: 0.6,
                    transition: 'opacity 0.2s'
                  }}
                >
                  <EditIcon sx={{ fontSize: '16px' }} />
                </IconButton>
              </Box>
            )}
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          display: { xs: 'flex', lg: 'none' },
          justifyContent: 'center',
          flexWrap: 'wrap',
          maxWidth: '450px'
        }}
      >
        <InsertPhotoOutlined
          sx={{ color: '#707070', width: '32px', height: '32px', cursor: 'pointer' }}
          onClick={() => setIsGalleryOpen(true)}
        />
        <Dialog open={isGalleryOpen} onClose={() => setIsGalleryOpen(false)}>
          <ImageGallery
            items={protocolImages}
            startIndex={currentImageIndex}
            showFullscreenButton={false}
            showPlayButton={false}
            disableSwipe={false}
            showThumbnails={true}
            showIndex={true}
            onSlide={handleSlide}
            renderCustomControls={() => {
              return (
                <DeleteOutline
                  sx={{
                    color: '#707070',
                    width: '34px',
                    height: '34px',
                    cursor: 'pointer',
                    position: 'absolute',
                    zIndex: 50,
                    backgroundColor: '#FFF',
                    top: '10px',
                    left: '10px',
                    borderRadius: '50px',
                    padding: '4px'
                  }}
                  onClick={() => {
                    const currentId = getCurrentImageId()
                    deleteProtocolRoomImages.mutate(currentId)
                  }}
                />
              )
            }}
          />
        </Dialog>
      </Box>
    </Box>
  )
}

export default ProtocolRoomImages
